'use client';

import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { api } from '@/lib/api';
import { Prompt } from '@/types';
import PromptGrid from '@/components/prompt/PromptGrid';
import SuggestedCuratorsBlock from '@/components/users/SuggestedCuratorsBlock';
import { useAuth } from '@/lib/auth-context';
import { useT } from '@/lib/i18n';
import { DEFAULT_SITE_CONFIG, useSiteConfig } from '@/lib/site-config';
import AdSlot from '@/components/ads/AdSlot';
// FeaturedCarousel was removed — its "Spotlight" section overlapped with the
// new HeroImageCarousel banner above. The image hero already does the
// "discover something new every visit" job, and one carousel above the
// gallery is plenty.
import HeroImageCarousel from '@/components/home/HeroImageCarousel';
import WelcomeBanner from '@/components/WelcomeBanner';
import InfiniteScrollSentinel from '@/components/common/InfiniteScrollSentinel';

type SortKey = 'trending' | 'newest' | 'top_rated';
const SORT_OPTIONS: { labelKey: string; value: SortKey }[] = [
  { labelKey: 'home.sort_featured', value: 'top_rated' },
  { labelKey: 'home.sort_newest',   value: 'newest' },
  { labelKey: 'home.sort_top',      value: 'trending' },
];

const MODEL_CHIPS = [
  { key: 'all',       labelKey: 'home.all_categories', color: '' },
  { key: 'Art',       labelKey: 'home.category_art',       color: 'oklch(0.72 0.15 285)' },  // purple-blue
  { key: 'Writing',   labelKey: 'home.category_writing',   color: 'oklch(0.68 0.16 25)'  },  // warm orange
  { key: 'Coding',    labelKey: 'home.category_coding',    color: 'oklch(0.70 0.16 160)' },  // teal-green
  { key: 'Business',  labelKey: 'home.category_business',  color: 'oklch(0.72 0.14 230)' },  // sky blue
  { key: 'Education', labelKey: 'home.category_education', color: 'oklch(0.72 0.17 55)'  },  // amber
];

/** Abbreviate large stat numbers: 12345 → "12.3k", 2100000 → "2.1M". */
function formatStat(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(n >= 10_000 ? 0 : 1) + 'k';
  return n.toLocaleString();
}

function GalleryPageInner() {
  const { user } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const t = useT();
  const { config } = useSiteConfig();

  // Admin-customised hero copy overrides the default i18n strings. We only
  // swap in the config values when they DIFFER from the shipped defaults,
  // so translation users keep getting their localised copy until the admin
  // customises anything.
  const heroBefore = config['hero.title_before'] !== DEFAULT_SITE_CONFIG['hero.title_before']
    ? config['hero.title_before']
    : t('home.hero_title_before');
  const heroEm = config['hero.title_em'] !== DEFAULT_SITE_CONFIG['hero.title_em']
    ? config['hero.title_em']
    : t('home.hero_title_em');
  const heroAfter = config['hero.title_after'] !== DEFAULT_SITE_CONFIG['hero.title_after']
    ? config['hero.title_after']
    : t('home.hero_title_after');
  const heroTagline = config['hero.tagline'] !== DEFAULT_SITE_CONFIG['hero.tagline']
    ? config['hero.tagline']
    : t('home.hero_tagline');

  const urlSearch = searchParams.get('search') ?? '';
  const urlCategory = searchParams.get('category') ?? '';
  const urlTags = searchParams.get('tags') ?? '';

  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeCategory, setActiveCategory] = useState(urlCategory);
  const [activeSort, setActiveSort] = useState<SortKey>('top_rated');
  const [search, setSearch] = useState(urlSearch);
  const [activeTags, setActiveTags] = useState(urlTags);
  const [layout, setLayout] = useState<'masonry' | 'uniform' | 'list'>('masonry');
  // Public platform stats — backend-backed, not hardcoded.
  const [publicStats, setPublicStats] = useState<{ prompts_shared: number; copies_this_month: number; creators: number } | null>(null);

  const SIZE = 20;

  useEffect(() => { setSearch(urlSearch); }, [urlSearch]);
  useEffect(() => { setActiveCategory(urlCategory); }, [urlCategory]);
  useEffect(() => { setActiveTags(urlTags); }, [urlTags]);
  useEffect(() => {
    const saved = localStorage.getItem('layout') as 'masonry' | 'uniform' | 'list' | null;
    if (saved) setLayout(saved);
    // Fetch hero stats once per mount — we don't need realtime updates here.
    api.stats.public().then(setPublicStats).catch(() => { /* non-critical */ });
  }, []);
  useEffect(() => { try { localStorage.setItem('layout', layout); } catch { /* ignore */ } }, [layout]);

  const fetchPrompts = useCallback(async (category: string, searchVal: string, tagCsv: string, sort: SortKey, p: number, append = false) => {
    if (!append) setLoading(true);
    try {
      const data = await api.prompts.list({
        category: category || undefined,
        search: searchVal || undefined,
        tags: tagCsv || undefined,
        sort,
        page: p,
        size: SIZE,
      });
      setPrompts((prev) => append ? [...prev, ...data.items] : data.items);
      setTotal(data.total);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    fetchPrompts(activeCategory, search, activeTags, activeSort, 1);
  }, [activeCategory, search, activeTags, activeSort, fetchPrompts]);

  // `loadingMore` is tracked separately from `loading`. `loading` only
  // covers the INITIAL fetch (or after a filter change); during pagination
  // the cards stay visible and we render a footer indicator instead. The
  // <InfiniteScrollSentinel> reads this so its IntersectionObserver
  // disconnects while a fetch is in flight and rearms after it lands.
  const [loadingMore, setLoadingMore] = useState(false);

  const handleLoadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    const next = page + 1;
    setPage(next);
    try {
      await fetchPrompts(activeCategory, search, activeTags, activeSort, next, true);
    } finally {
      setLoadingMore(false);
    }
  };

  const clearTags = () => {
    setActiveTags('');
    const sp = new URLSearchParams(searchParams.toString());
    sp.delete('tags');
    const qs = sp.toString();
    router.replace(qs ? `/?${qs}` : '/');
  };

  const activeTagList = activeTags ? activeTags.split(',').map((t) => t.trim()).filter(Boolean) : [];

  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updateSearchInUrl = (value: string) => {
    setSearch(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => {
      const sp = new URLSearchParams(searchParams.toString());
      if (value) sp.set('search', value); else sp.delete('search');
      const qs = sp.toString();
      router.replace(qs ? `/?${qs}` : '/');
    }, 350);
  };

  const setCategoryInUrl = (cat: string) => {
    setActiveCategory(cat);
    const sp = new URLSearchParams(searchParams.toString());
    if (cat) sp.set('category', cat); else sp.delete('category');
    const qs = sp.toString();
    router.replace(qs ? `/?${qs}` : '/');
  };

  const hasMore = prompts.length < total;

  return (
    <>
      {/* Top-of-page image banner — auto-cycles through visually rich prompts.
          Sits above the brand hero so the very first thing visitors see is
          actual user-generated content. Hidden automatically when no prompts
          have cover images yet (fresh installs). */}
      <HeroImageCarousel />

      {/* Welcome banner — first 7 days, dismissable; nothing for veterans. */}
      <div className="page-pad" style={{ maxWidth: 1200, margin: '24px auto 0' }}>
        <WelcomeBanner />
      </div>

      {/* Hero */}
      <section
        className="hero page-pad"
        style={{
          paddingTop: 56,
          paddingBottom: 40,
          maxWidth: 1200,
          // Reserve hero block height to prevent layout shift when web fonts
          // (Fraunces serif + Inter body) finish loading and reflow the text.
          // Lighthouse: 0.332 CLS came from this section. clamp() matches the
          // post-load height across mobile->desktop. Slack of ~20px on most
          // devices, never visible blank space.
          minHeight: 'clamp(280px, 32vh, 360px)',
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontWeight: 400,
            fontVariationSettings: '"opsz" 110, "SOFT" 50',
            fontSize: 'clamp(44px, 5.2vw, 72px)',
            lineHeight: 1,
            letterSpacing: '-0.035em',
            margin: '0 0 20px',
            color: 'var(--ink)',
            textWrap: 'balance',
          }}
        >
          {heroBefore}{' '}
          <em style={{ fontStyle: 'italic', color: 'var(--accent)', fontWeight: 400 }}>
            {heroEm}
          </em>{' '}
          {heroAfter}
        </h1>
        <p style={{
          fontSize: 16, lineHeight: 1.55, color: 'var(--ink-2)',
          maxWidth: 560, margin: '0 0 28px', textWrap: 'pretty',
        }}>
          {heroTagline}
        </p>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Link
            href={user ? '/create' : '/auth/login'}
            className="ps-btn ps-btn-primary no-underline"
          >
            {t('home.start_exploring')}
            <span className="material-symbols-outlined ps-icon">arrow_outward</span>
          </Link>
          <Link
            href={user ? '/create' : '/auth/login'}
            className="ps-btn ps-btn-secondary no-underline"
          >
            <span className="material-symbols-outlined ps-icon">add</span>
            {t('home.share_a_prompt')}
          </Link>
        </div>
        <div style={{
          display: 'flex', gap: 32, marginTop: 40, paddingTop: 28,
          borderTop: '1px solid var(--line)', maxWidth: 700,
        }}>
          <HeroStat n={formatStat(publicStats?.prompts_shared ?? total)} l={t('home.stat_prompts_shared')} />
          <HeroStat n={formatStat(publicStats?.copies_this_month ?? 0)} l={t('home.stat_copies_month')} />
          <HeroStat n={formatStat(publicStats?.creators ?? 0)} l={t('home.stat_creators')} />
        </div>
      </section>

      {/* Section header */}
      <div className="section-hd">
        <div>
          <div className="eyebrow">{t('home.section_eyebrow')}</div>
          <h2>{activeCategory ? t('home.category_prompts', { category: activeCategory }) : t('home.featured_prompts')}</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="seg" role="tablist" aria-label="Sort">
            {SORT_OPTIONS.map((s) => (
              <button
                key={s.value}
                aria-pressed={activeSort === s.value}
                onClick={() => setActiveSort(s.value)}
              >
                {t(s.labelKey)}
              </button>
            ))}
          </div>
          <div className="seg" role="tablist" aria-label="Layout">
            <button aria-pressed={layout === 'masonry'} onClick={() => setLayout('masonry')}
              title={t('home.layout_masonry')} style={{ padding: '5px 8px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: -2 }}>dashboard</span>
            </button>
            <button aria-pressed={layout === 'uniform'} onClick={() => setLayout('uniform')}
              title={t('home.layout_uniform')} style={{ padding: '5px 8px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: -2 }}>grid_view</span>
            </button>
            <button aria-pressed={layout === 'list'} onClick={() => setLayout('list')}
              title={t('home.layout_list')} style={{ padding: '5px 8px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: -2 }}>view_list</span>
            </button>
          </div>
        </div>
      </div>

      {/* Category chips */}
      <div className="chip-row">
        {MODEL_CHIPS.map((c) => (
          <button
            key={c.key}
            className="chip"
            aria-pressed={(c.key === 'all' && !activeCategory) || activeCategory === c.key}
            onClick={() => setCategoryInUrl(c.key === 'all' ? '' : c.key)}
          >
            {c.color && <span className="model-dot" style={{ background: c.color }} />}
            {t(c.labelKey)}
          </button>
        ))}
        <div style={{ width: 1, height: 18, background: 'var(--line)', margin: '0 4px' }} />
        {activeTagList.length > 0 ? (
          <>
            {activeTagList.map((t) => (
              <span key={t} className="chip" aria-pressed="true">#{t}</span>
            ))}
            <button className="chip" onClick={clearTags} style={{ color: 'var(--ink-3)' }}>
              <span className="material-symbols-outlined" style={{ fontSize: 12 }}>close</span>
              {t('home.clear_tags')}
            </button>
          </>
        ) : (
          <Link href="/tags" className="chip no-underline" style={{ color: 'var(--ink-3)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 12 }}>sell</span>
            {t('home.browse_tags')}
          </Link>
        )}
        <div style={{ marginLeft: 'auto' }}>
          {/* Mobile search */}
          <div className="lg:hidden" style={{ display: 'flex', gap: 6 }}>
            <input
              value={search}
              onChange={(e) => updateSearchInUrl(e.target.value)}
              placeholder={t('home.search_placeholder')}
              style={{
                padding: '5px 11px', fontSize: 12,
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--bg-raised)',
                color: 'var(--ink)',
                minWidth: 160,
              }}
            />
          </div>
        </div>
      </div>

      {/* Sponsored slot — sits between the hero and the gallery on home.
          Auto-hides when AdSense is disabled in admin → Config. The slot id
          below is a placeholder; create the real one in your AdSense
          dashboard and update via env or hardcode here. */}
      <div className="page-pad" style={{ paddingBottom: 32, maxWidth: 1100 }}>
        <AdSlot slot="home-hero-leaderboard" format="horizontal" />
      </div>

      {/* Suggested curators — only for logged-in viewers, as a discovery nudge. */}
      {user && (
        <div className="page-pad" style={{ paddingBottom: 56 }}>
          <SuggestedCuratorsBlock variant="home-widget" limit={6} />
        </div>
      )}

      {/* Grid */}
      {loading || prompts.length > 0 ? (
        <div className="page-pad" style={{ paddingBottom: 48 }}>
          <PromptGrid
            prompts={prompts}
            loading={loading}
            layout={layout}
            emptyMessage={`${t('home.empty_no_prompts')}. ${t('home.empty_try_clearing')}`}
          />
          {/* Auto-loads when scrolled near the bottom (400px pre-load).
              Three-dot pulse renders while the fetch is in flight; "you've
              reached the end" caption renders when there's nothing left. */}
          {!loading && (
            <InfiniteScrollSentinel
              hasMore={hasMore}
              loading={loadingMore}
              onLoadMore={handleLoadMore}
            />
          )}
        </div>
      ) : (
        <div className="ps-empty">
          <h3>{t('home.empty_no_prompts')}</h3>
          <p>{t('home.empty_try_clearing')}</p>
        </div>
      )}
    </>
  );
}

function HeroStat({ n, l }: { n: string; l: string }) {
  return (
    <div>
      <div style={{
        fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 400,
        letterSpacing: '-0.02em', color: 'var(--ink)',
        fontVariantNumeric: 'tabular-nums', lineHeight: 1.05,
      }}>
        {n}
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2, letterSpacing: '0.01em' }}>
        {l}
      </div>
    </div>
  );
}

export default function GalleryPage() {
  return (
    <Suspense fallback={<div />}>
      <GalleryPageInner />
    </Suspense>
  );
}
